import React, {useEffect, useRef, useState} from "react";
import {
    Avatar,
    Checkbox, Chip,
    FormControl,
    FormControlLabel,
    Grid,
    Link,
    MenuItem,
    Paper,
    TextField
} from "@material-ui/core"
import {CameraAlt, Cancel} from "@material-ui/icons";
import SingleAsyncAutoComplete from "../../../shared/SingleAsyncAutoComplete";
import useDataFetchers from "../../../../../hooks/useDataFetchers";
import {useField, useFieldWithValidation} from "../../../../../hooks";
import _ from "lodash";
import {
    badEmails,
    formatDateUS,
    fromArrayToOrSentence,
    validEmail,
    validLinkedInUrl
} from "../../../../../helpers/utils";
import InputComboBox from "../../../sharedFormComponents/InputComboBox";
import {DropzoneArea} from "material-ui-dropzone";
import {nameSuffixes, PERSON_TAKEN_AT_FIRM_MESSAGE} from "../../../contacts/ContactConstants";
import SingleAutoComplete from "../../../shared/SingleAutoComplete";
import useYears from "../../../../../hooks/useYears";
import UrlTextField from "../../../shared/Inputs/UrlTextField";
import {mergeById, mergeImportedRows} from "../../../../../helpers/affinityRowMerge";
import {matchedAvatarUrl, onUnreachablePhoto} from "../../../../../helpers/matchPhoto";
import ContactActions from "../../../shared/Buttons/ContactActions";
import NotesDialog from "../../../shared/Dialogs/NotesDialog";
import useAdminNotes from "../../../../../hooks/useAdminNotes";
import ContactService from "../../../services/ContactService";
import ChangeLogModal from "../../../shared/ChangeLogModal";
import {useHistory} from "react-router-dom";
import AssignDialog from "../../../shared/Dialogs/AssignDialog";
import {ChipLabel} from "../../../shared/Chips";
import useAsyncRequest from "../../../../../hooks/useAsyncRequest";
import ContactDraftService from "../../../services/ContactDraftService";
import RiaContactWorkExperienceRow from "../../../foundations/contactDrafts/form/sections/RiaContactWorkExperienceRow";
import EmailTextField from "../../../shared/Inputs/EmailTextField";
import PhoneTextField from "../../../shared/Inputs/PhoneTextField";
import usePersonMatch from "../../../../../hooks/usePersonMatch";
import PersonMatchDialog from "../../../shared/Dialogs/PersonMatchDialog";
import PersonPreviewModal from "../../../shared/PersonPreviewModal";
import DesignationField from "../../../shared/Inputs/DesignationField";

// The firm form posts a nested contact's state verbatim, so only Contact columns may be written
// onto it. The rest of the match is person-level and raises an unknown attribute on save.
const IMPORTED_CONTACT_FIELDS = [
    'people_id', 'first_name', 'last_name', 'name_suffix', 'gender', 'linkedin_url',
    'achievements', 'contact_crd_id',
];

const Contact = ({
                     session, contactData, index,
                     contacts, setContacts, genders,
                     responsibilities, handleOnError, removeContactError, isEditing,
                     isNew, drafts, snackbar, dataType, setUserMadeChanges, deleter, investorId,
                     originalLinkedinUrl, ...props }) => {
    const [contact, setContact] = useState({ ...contactData });
    const [selectedPosition, setSelectedPosition] = useState({});
    // The url an accepted match brought in. Once a person is linked it, not the row's own url, is
    // what a later refusal has to put back - a new row has none of its own, and leaving the
    // declined candidate on the contact writes it onto the matched person on save.
    const matchedLinkedinUrl = useRef(null);
    const preMatchContact = useRef(null);
    const handleUserChanges = () => {
        setUserMadeChanges(true)
    }
    const {reset: resetNotes, ...notes} = useField(
        'text',
        false,
        contact.contact_notes || "",
        handleUserChanges,
    );
    const {reset: resetFirstName, validate: validateFirstName, ...first_name} = useFieldWithValidation(
        "text",
        contact.first_name || "",
        (value) => (!_.isEmpty(value)),
        true,
        handleUserChanges,
    );
    const {reset: resetLastName, validate: validateLastName, ...last_name} = useFieldWithValidation(
        "text",
        contact.last_name || "",
        (value) => (!_.isEmpty(value)),
        true,
        handleUserChanges,
    );
    const {reset: resetEmail, validate: validateEmail, ...email} = useFieldWithValidation(
        "text",
        contact.email || "",
        (value) => (validEmail(value) && value && !badEmails.some(x => value.startsWith(x))),
        true,
        handleUserChanges,
        (value) => (value && badEmails.some(x => value.startsWith(x))) ? `Email is not allowed. It cannot starts with ${fromArrayToOrSentence(badEmails)}` : null
    );
    const {reset: resetPhone, validate: validatePhone, ...phone} = useFieldWithValidation(
        "text",
        contact.phone || "",
        (value) => (!_.isEmpty(value)),
        true,
        handleUserChanges,
    );
    const {reset: resetLinkedIn, validate: validateLinkedIn, ...linkedin_url} = useFieldWithValidation(
        "text",
        contact.linkedin_url || "",
        (value) => (validLinkedInUrl(value) || _.isEmpty(value)),
        true,
        handleUserChanges,
    );
    const {reset: resetBiography, ...biography} = useField(
        'text',
        true,
        contact.biography || "",
        handleUserChanges,
    );
    const resource = drafts ? "ContactDraft" : "Contact";
    const service = drafts ? ContactDraftService : ContactService;
    // A draft has no person, so the names are what it saves and what approval carries over.
    // Tokens outside the catalog have no chip, so they are appended or the save would drop them.
    const designationFields = (achievements, unmatchedDesignations = []) => ({
        achievements,
        achievement_ids: achievements.map(({id}) => (id)),
        ...(drafts ? {designation: [...achievements.map(({name}) => name), ...unmatchedDesignations].join(', ')} : {}),
    });
    const [titleError, setTitleError] = useState(!isEditing);
    const fetchers = useDataFetchers(session, dataType);
    const adminNotes = useAdminNotes(contactData.id, resource, snackbar, service.notes);
    const [openChanges, setOpenChanges] = useState(false);
    const [changes, setChanges] = useState(null);
    const history = useHistory();
    const [openInvestors, setOpenInvestors] = useState(false);
    const [investorSelected, setInvestorSelected] = useState();

    // Drafts have no person link until they are approved, so there is nothing to match.
    const matchEnabled = !drafts

    const [riaWorks, setRiaWorks] = useState([])

    // Registration history belongs to the CRD, so it is read whenever the row carries one.
    // contact_drafts has no crd column, so a draft never asks.
    const crdId = contact?.contact_crd_id || null

    useEffect(() => {
        if (!crdId || !session) {
            setRiaWorks([])
            return
        }

        ContactService.riaWorks({crd_id: crdId}, session).then((response) => {
            setRiaWorks(response.data.works || []);
        });
    }, [crdId, session]);

    // useYears(true) option ids are strings; the match carries integers, which match no option -
    // the start year renders blank and the end year falls back to "Present".
    const yearOption = (year) => (year === null || year === undefined ? '' : String(year))

    // UniversityData/CompanyData read `name`; the match spells it university_name/company_name.
    const affinityRows = (rows, idField, nameField) => (rows || []).map((row, i) => ({
        [idField]: row[idField],
        name: row[nameField],
        start_year: yearOption(row.start_year),
        end_year: yearOption(row.end_year),
        id: undefined,
        _destroy: false,
        key: Date.now() + i,
    }))

    const acceptMatch = (match) => {
        if (!contact.matched_people_id) {
            preMatchContact.current = contact
        }
        // Kept rows were keyed by their id, which the merge drops.
        const mergeAffinityRows = (currentRows, importedRows, idField) => mergeImportedRows(currentRows, importedRows, idField)
            .map((row, i) => ({...row, key: row.key ?? `kept-${Date.now()}-${i}`}))
        const universities = mergeAffinityRows(
            preMatchContact.current.university,
            affinityRows(match.universities, 'university_id', 'university_name'),
            'university_id',
        )
        const companies = mergeAffinityRows(
            preMatchContact.current.company,
            affinityRows(match.companies, 'company_id', 'company_name'),
            'company_id',
        )
        // The avatar renders from photoPreview; the match brings the photo as `photo`.
        const currentUrl = preMatchContact.current.photoPreview
        const matchUrl = match?.photo?.url
        const avatarUrl = matchedAvatarUrl({currentUrl, matchUrl})
        matchedLinkedinUrl.current = match?.linkedin_url || null
        const achievements = mergeById(preMatchContact.current.achievements, match.achievements)

        setContact(prevState => ({
            ...prevState,
            ..._.pick(match, IMPORTED_CONTACT_FIELDS),
            matched_people_id: match.people_id,
            // The section owns the cross-dataset prompt and reads the row, not this ref, so the
            // url the match brought in rides along - the prompt is skipped only while it holds.
            matched_linkedin_url: match?.linkedin_url || null,
            // The section renders these; the save reads their *_attributes mirrors.
            university: universities,
            contact_universities_attributes: universities,
            company: companies,
            company_contacts_attributes: companies,
            photoPreview: avatarUrl,
            photo_url: avatarUrl,
            ...designationFields(achievements),
        }))
        handleUserChanges()
        onUnreachablePhoto(matchUrl, () => setContact(prevState => (
            prevState.photoPreview === matchUrl ? {...prevState, photoPreview: currentUrl, photo_url: currentUrl} : prevState
        )))
    }

    // A refused match must not leave somebody else's url behind. An edited contact goes back to
    // the url it had - onto the contact too, or the save still carries the refused one.
    const restoreLinkedinUrl = () => {
        const restored = matchedLinkedinUrl.current || originalLinkedinUrl

        if (restored) {
            setContact(prevState => ({...prevState, linkedin_url: restored}))
            return
        }

        // Blank was the row's own value, so posting it changes nothing on the person - while
        // leaving the refused url on the contact would write it there.
        resetLinkedIn()
        setContact(prevState => ({...prevState, linkedin_url: ''}))
    }

    // A url typed over an accepted match that resolves to nobody leaves that match behind, so the
    // import is undone - otherwise the save writes the previous person's fields next to the new url.
    const revertMatch = (linkedin) => {
        if (!contact.matched_people_id || linkedin === matchedLinkedinUrl.current) return;

        const preMatch = preMatchContact.current
        matchedLinkedinUrl.current = null
        preMatchContact.current = null
        setContact({...preMatch, linkedin_url: linkedin})
    }

    const declineMatch = () => restoreLinkedinUrl()

    const personMatch = usePersonMatch({
        findMatch: (params) => ContactService.findMatch(params, session),
        baseParams: () => ({contact_id: contact.id, investor_id: contact.investor_id || investorId}),
        onAccept: acceptMatch,
        onDecline: declineMatch,
    })

    const handleFindMatch = (linkedin) => {
        if (!matchEnabled) return;
        if (!linkedin) {
            personMatch.cancel()
            revertMatch('')
            return;
        }

        personMatch.request({linkedin_url: linkedin}).then(({match, already_exist}) => {
            if (match) {
                personMatch.show(match)
            } else if (already_exist) {
                // An edited row renders inside the modal, so the message goes through the
                // section's snackbar, which sits outside it and serves new rows too.
                snackbar.show(PERSON_TAKEN_AT_FIRM_MESSAGE, 'warning', true)
                restoreLinkedinUrl()
            } else {
                revertMatch(linkedin)
            }
        })
    }

    const requiredErrors = () => {
        return first_name.error || last_name.error || email.error || titleError
            || phone.error || (_.isEmpty(contact.gender)) || biography.error
    }



    useEffect(() => {
        handleOnError(requiredErrors());
    }, [
        first_name.error, last_name.error, email.error, titleError,
        phone.error, contact.gender, biography.error
    ]);

    useEffect(() => {
        if (contact._destroy) {
            const newContacts = contacts.filter((contact, i) => index !== i);
            setContacts(newContacts)
            removeContactError(index)
        } else if (!_.isNull(contact)) {
            let newContacts = [...contacts]
            newContacts[index] = contact;
            setContacts(newContacts);
        }
    }, [contact])

    useEffect(() => {
        setSelectedPosition({ id: contactData?.position_id, name: contactData?.position_name });

        setContact(prevState => ({
            ...prevState,
            photoPreview: contactData?.photo_url || '',
        }));
    }, [])

    const handleOnChangeAutocomplete = (field, selectedValue) => {
        handleUserChanges()
        let fieldData = {};

        switch (field) {
            case 'position_id':
                fieldData = {
                    position_id: selectedValue?.id,
                    position_name: selectedValue?.name,
                }

                setSelectedPosition(selectedValue);
                break;
        }

        setContact(prevState => ({
            ...prevState,
            ...fieldData,
        }));
    }

    const handleOnChange = (event) => {
        handleUserChanges()
        event.persist();

        setContact(prevState => ({
            ...prevState,
            [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
        }));
    }

    const handleChangeFileInput = async (files) => {
        const file = files[0];
        if (isNew) {
            setContact(prevState => ({
                ...prevState,
                newPhoto: file,
                photoPreview: file ? URL.createObjectURL(file) : contact.photoPreview,
                photo_url: file ? URL.createObjectURL(file) : contact.photoPreview,
            }));
        } else {
            if (file) {
                snackbar.show('The photo is uploading please wait', 'success', true);
                const {data} = await service.uploadAvatar(contact.id, session, file)
                if (data?.photo?.url) {
                    setContact(prevState => ({
                        ...prevState,
                        newPhoto: data?.photo?.url,
                        photoPreview: data?.photo?.url,
                        photo_url: data?.photo?.url,
                    }));
                } else {
                    snackbar.show('This photo is not supported please try another one', 'warning', true);
                }
            }
        }
    }

    const handleRemoveContact = async () => {
        const contactName = contact.first_name ? `${contact.first_name} ${contact.last_name}` : 'this contact';
        if (window.confirm(`Are you sure you want to delete ${contactName}?`)) {
            if (contact.id) {
                await deleter.contacts(contact);
            }

            setContact(prevState => ({
                ...prevState,
                _destroy: true,
            }));
            if (props.setSelectedContact) {
                props.setSelectedContact(null)
            }
        }
    }

    const handleRemoveFromMultiple = (event, value, field) => {
        event.persist();

        let newData = [...contact[field]];

        newData = newData.filter((datumId) => datumId !== value);

        setContact(prevState => ({
            ...prevState,
            [field]: newData,
        }));
    }

    const handleAddUniversity = () => {
        handleUserChanges()
        let newUniversities = [...contact.university];

        newUniversities.push({
            university_id: '',
            start_year: '',
            end_year: '',
            key: Date.now(),
            id: undefined,
            _destroy: false,
        })

        setContact(prevState => ({
            ...prevState,
            university: newUniversities,
        }));
    }

    const handleAddCompany = () => {
        handleUserChanges()
        let newCompanies = [...contact.company];

        newCompanies.push({
            company_id: '',
            start_year: '',
            end_year: '',
            key: Date.now(),
            id: undefined,
            _destroy: false,
        })

        setContact(prevState => ({
            ...prevState,
            company: newCompanies,
        }));
    }

    const errorMessage = {};

    const handleOpenChangeLog = () => {
        service.changeLog(contact.id, session).then((response) => {
            const {changes} = response.data;
            setOpenChanges(true)
            setChanges(changes);
        });
    }

    const handleOpenInvestors = () => setOpenInvestors(true);
    const handleCloseInvestors = () => setOpenInvestors(false);

    const handleMoveCovered = useAsyncRequest(
        () => ContactService.moveToInvestor(contact.id, session, investorSelected.id),
        (data) => {
            setUserMadeChanges(false);
            history.push(`/admin/investors?data_type=${dataType}&investor_id=${investorSelected.id}&contact_id=${contact.id}`);
        },
        (error) => snackbar.show(error.message, 'warning'),
    )

    const handleMoveNonCovered = useAsyncRequest(
        () => ContactService.noLongerWithFirm(contact.id, session),
        (data) => {
            setUserMadeChanges(false);
            history.push(`/admin/contacts?data_type=${dataType}&contact_id=${contact.id}`);
        },
        (error) => snackbar.show(error.message, 'warning'),
    )

    const handleApproveMove = useAsyncRequest(
        () => ContactService.approveMove(contact.id, session),
        (data) => {
            setContact(prevState => ({...prevState, pending_for_approval: false}))
            snackbar.show("Contact Movement approved", "success")
        },
        (error) => snackbar.show(error.message, 'warning'),
    )

    const handleMarkAsReviewed = useAsyncRequest(
        () => ContactService.markAsReviewed(contact.id, session),
        (data) => {
            setContact(prevState => ({...prevState, last_reviewed: data.last_reviewed}))
            snackbar.show("Contact Mark as reviewed", "success")
        },
        (error) => snackbar.show(error.message, 'warning'),
    )

    const years = useYears(true);

    const handleOnChangePhoneExt = (e) => {
      const digitsOnly = e.target.value.replace(/\D/g, '');
      e.target.value = session?.isSuperAdmin ? digitsOnly : digitsOnly.slice(0, 6);
      handleOnChange(e);
    }

    const RenderContactContainer = () => {
        return <Grid container spacing={3} className='admin-contact-fields'>
            <Grid item xs={3} >
                { isNew && "Contact Form"}
            </Grid>
            <Grid item xs={3} style={{display: "flex", alignItems: "center"}}>
                {
                    contact.pending_for_approval && <ChipLabel label={"Movement pending for approval"} className={"data-access-chip error"}/>
                }
            </Grid>
            <Grid item xs={6} style={{display: "flex", justifyContent: "flex-end"}}>
                <ContactActions
                    drafts={drafts}
                    isNew={isNew}
                    pendingApproval={contact.pending_for_approval}
                    handlers={{
                        notes: () => adminNotes.setOpen(true),
                        changeLog: handleOpenChangeLog,
                        remove: handleRemoveContact,
                        moveCovered: handleOpenInvestors,
                        moveNonCovered: handleMoveNonCovered,
                        approve: handleApproveMove,
                        markAsReviewed: handleMarkAsReviewed,
                    }}
                />
            </Grid>

            <Grid item xs={2}>
                <div className='user-avatar-container'>
                    <Avatar src={contact?.photoPreview} className="photo" alt="user-avatar" />
                    <DropzoneArea
                        acceptedFiles={['image/*']}
                        dropzoneText={"Upload Image"}
                        Icon={CameraAlt}
                        filesLimit={1}
                        showPreviews={false}
                        showAlerts={false}
                        onDrop={(files) => handleChangeFileInput(files)}
                        styles={{ dropzone: { minHeight: 200, maxHeight: 250 } }}
                    />
                </div>
            </Grid>

            <Grid container item spacing={2} xs={10}>
                {
                    !isNew && <>
                        <Grid item xs={4}>
                            Created At: {formatDateUS(contact.created_at)}
                        </Grid>
                        <Grid item xs={4}>
                            Updated At: {formatDateUS(contact.updated_at)}
                        </Grid>
                        <Grid item xs={4}>
                            Last Reviewed: {formatDateUS(contact.last_reviewed)}
                        </Grid>
                    </>
                }

                {
                    drafts && <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                variant="outlined"
                                {...notes}
                                onBlur={handleOnChange}
                                label={"Notes"}
                                name={"contact_notes"}
                            />
                            <span>{errorMessage['contact_notes']}</span>
                        </FormControl>
                    </Grid>
                }

                <Grid item xs={2}>
                    <InputComboBox
                        value={contact.gender}
                        label={'Gender'}
                        field={'gender'}
                        name={'gender'}
                        handleOnChange={handleOnChange}
                        options={genders.map((gender) => ({id: gender, name: gender}))}
                        error={_.isEmpty(contact.gender)}
                    />
                </Grid>

                <Grid item xs={4}>
                    <FormControl fullWidth>
                        <TextField
                            variant="outlined"
                            {...first_name}
                            onBlur={handleOnChange}
                            label={"First Name"}
                            name={"first_name"}
                            maxLength={40}
                        />
                        <span>{errorMessage['first_name']}</span>
                    </FormControl>
                </Grid>

                <Grid item xs={4}>
                    <FormControl fullWidth>
                        <TextField
                            variant="outlined"
                            {...last_name}
                            onBlur={handleOnChange}
                            label={"Last Name"}
                            name={"last_name"}
                            maxLength={40}
                        />
                        <span>{errorMessage['last_name']}</span>
                    </FormControl>
                </Grid>

                <Grid item xs={2}>
                    <InputComboBox
                        value={contact.name_suffix}
                        label={'Suffix'}
                        field={'name_suffix'}
                        name={'name_suffix'}
                        handleOnChange={handleOnChange}
                        options={nameSuffixes}
                    />
                </Grid>
            </Grid>

            <Grid item xs={4}>
                <SingleAsyncAutoComplete
                    fetch={fetchers.titles}
                    handleOnChange={(value) => handleOnChangeAutocomplete('position_id', value)}
                    handleOnError={(error) => setTitleError(error)}
                    value={selectedPosition}
                    label={'Title'}
                    field={'position_id'}
                    required
                />
                <span>{errorMessage['position_id']}</span>
            </Grid>

            <Grid item xs={4}>
                <UrlTextField
                    variant="outlined"
                    {...linkedin_url}
                    onBlur={(event) => { handleOnChange(event); handleFindMatch(event.target.value); }}
                    label={"LinkedIn Url"}
                    name={"linkedin_url"}
                    maxLength={150}
                    helperText="Please use format https://www.linkedin.com/in/username/"
                />
            </Grid>

            <Grid item xs={4}>
                <FormControl fullWidth>
                    <DesignationField
                        selectedValues={contact.achievements || []}
                        handleOnChange={(_event, value) => {
                            handleUserChanges()
                            setContact(prevState => ({
                                ...prevState,
                                ...designationFields(value, prevState.unmatched_designations),
                            }))
                        }}
                    />
                </FormControl>
            </Grid>

            <Grid item xs={4}>
                <FormControl fullWidth>
                    <EmailTextField
                        {...email}
                        value={email.value}
                        fullWidth
                        label="Email"
                        name="email"
                        variant="outlined"
                        maxLength={255}
                        clearEmail={resetEmail}
                        afterOnBlur={handleOnChange}
                        validationData={contactData?.email_validation_data}
                        entityData={{entity_id: contact.id, entity_type: drafts ? "ContactDraft" : "Contact", email_field: "email"}}
                        dataSet={'FO'}
                    />
                </FormControl>
            </Grid>

          <Grid item xs={3}>
                <FormControl fullWidth>
                    <PhoneTextField
                        {...phone}
                        fullWidth
                        label="Phone"
                        name="phone"
                        variant="outlined"
                        maxLength={40}
                        clearPhone={resetPhone}
                        dataSet={'FO'}
                        afterOnBlur={handleOnChange}
                        />
                    <span>{errorMessage['phone']}</span>
                </FormControl>
            </Grid>

            <Grid item xs={2}>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  label="Extension"
                  name="phone_ext"
                  variant="outlined"
                  inputProps={session?.isSuperAdmin ? {} : { maxLength: 6 }}
                  onChange={handleOnChangePhoneExt}
                  value={contact.phone_ext}
                />
              </FormControl>
            </Grid>

            <Grid item xs={3}>
                <FormControl fullWidth>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={
                                    contact.investment_committee || false
                                }
                                name="investment_committee"
                                color="primary"
                                onChange={handleOnChange}
                                value={true}
                            />
                        }
                        label="Investment Committee"
                    />
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    {...biography}
                    fullWidth
                    label="Biography"
                    variant="outlined"
                    name={'biography'}
                    multiline={true}
                    maxRows={16}
                    minRows={8}
                    onBlur={handleOnChange}
                />
                <span>{errorMessage['biography']}</span>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Additional Notes"
                    variant="outlined"
                    name={'additional_notes'}
                    onChange={handleOnChange}
                    value={contact.additional_notes}
                    multiline={true}
                    maxRows={4}
                    minRows={4}
                />
                <span>{errorMessage['additional_notes']}</span>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Private Bio"
                    variant="outlined"
                    name={'private_bio'}
                    onChange={handleOnChange}
                    value={contact.private_bio}
                    multiline={true}
                    maxRows={16}
                    minRows={8}
                />
                <span>{errorMessage['private_bio']}</span>
            </Grid>

            <Grid item xs={12}>
                <FormControl fullWidth>
                    <TextField
                        select
                        label='Contact Responsibilities'
                        fullWidth
                        variant='outlined'
                        name='contact_responsibilities_ids'
                        className=''
                        SelectProps={{
                            multiple: true,
                            value: contact.contact_responsibilities_ids || [],
                            onChange: handleOnChange,
                            MenuProps: {
                                PaperProps: {
                                    style: {
                                        maxHeight: '300px',
                                    },
                                },
                            },
                            renderValue: (selected) => {
                                return (
                                    <div className=''>
                                        {responsibilities.filter((responsibility) => selected.includes(responsibility.id)).map((responsibility) => (
                                            <Chip
                                                key={responsibility.id}
                                                label={responsibility.name}
                                                className=''
                                                clickable
                                                deleteIcon={
                                                    <Cancel
                                                        onMouseDown={(event) => event.stopPropagation()}
                                                    />
                                                }
                                                onDelete={(e) => handleRemoveFromMultiple(e, responsibility.id, 'contact_responsibilities_ids')}
                                            />
                                        ))}
                                    </div>
                                )
                            }
                        }}
                    >
                        <MenuItem value="">
                            <em>Chose Responsibilities</em>
                        </MenuItem>
                        {
                            responsibilities.map((responsibility, index) => <MenuItem key={`${responsibility.name}-${responsibility.id}`} value={responsibility.id}>{responsibility.name}</MenuItem>)
                        }
                    </TextField>
                    <span>{errorMessage['contact_responsibilities_ids']}</span>
                </FormControl>
            </Grid>

            <Grid item xs={12} className='admin-item-section-title'>
                <div>
                    Universities
                </div>

                <div>
                    <Link onClick={handleAddUniversity}>Add New University</Link>
                </div>
            </Grid>
            <Grid item container spacing={2} className='admin-item-section-container admin-contact-universities-fields'>
                {
                    contact.university?.map((universityData, index) =>
                        <UniversityData
                            key={universityData.id || universityData.key}
                            universityData={universityData}
                            universities={contact.university}
                            setContact={setContact}
                            index={index}
                            years={years}
                            session={session}
                            handleUserChanges={handleUserChanges}
                        />
                    )
                }
            </Grid>

            {/* Hidden for now; RIA Previous Work Experience is expected to come back.
            {crdId && riaWorks.length > 0 ?
                <React.Fragment>
                    <Grid item xs={12} className='admin-item-section-title'>
                        <div>
                            RIA Previous Work Experience
                        </div>
                    </Grid>
                    <Grid item container spacing={2} className='admin-item-section-container'>
                        {
                            riaWorks.map((work) =>
                                <Grid item xs={12} key={`ria-work-${work.id}`}
                                      style={{borderBottom: '1px solid #707070', padding: '20px'}}>
                                    <RiaContactWorkExperienceRow searchKeywords={{}} work={work}/>
                                </Grid>)
                        }
                    </Grid>
                </React.Fragment> : null
            } */}

            <Grid item xs={12} className='admin-item-section-title'>
                <div>
                    Companies
                </div>

                <div>
                    <Link onClick={handleAddCompany}>Add New Company</Link>
                </div>
            </Grid>
            <Grid item container spacing={2} className='admin-item-section-container admin-contact-companies-fields'>
                {
                    contact.company?.map((companyData, index) =>
                        <CompanyData
                            key={companyData.id || companyData.key}
                            companyData={companyData}
                            companies={contact.company}
                            setContact={setContact}
                            index={index}
                            years={years}
                            session={session}
                            handleUserChanges={handleUserChanges}
                        />
                    )
                }
            </Grid>

            {matchEnabled && <>
                <PersonMatchDialog {...personMatch.dialogProps} />
                <PersonPreviewModal {...personMatch.previewProps} />
            </>}

            <>
                <AssignDialog
                    fetch={fetchers.investors}
                    open={openInvestors}
                    body={""}
                    label={"Investor"}
                    handleClose={handleCloseInvestors}
                    handleSave={handleMoveCovered}
                    selected={investorSelected}
                    setSelected={setInvestorSelected}
                    submitText={"Continue"}
                />

                <NotesDialog
                    label={"Add a Note"}
                    body={""}
                    adminNotes={adminNotes}
                />

                {
                    openChanges && (
                        <ChangeLogModal open={openChanges} onClose={() => setOpenChanges(false)} changes={changes}
                                        changedBy={"Hubspot Sync"}/>
                    )
                }
            </>
        </Grid>
    }

    return (
        contact._destroy === true ? null :
            <>
                {
                    isNew ? <Paper elevation={3} className={"paper-sub-form"}>
                        {RenderContactContainer()}
                    </Paper> : RenderContactContainer()
                }
            </>
    )
}

const UniversityData = ({ universityData, universities, setContact, index, years, session, handleUserChanges }) => {
    const [university, setUniversity] = useState({ ...universityData });
    const [selectedUniversity, setSelectedUniversity] = useState({});
    const fetchers = useDataFetchers(session);

    useEffect(() => {
        let newUniversities = [...universities]
        newUniversities[index] = university;
        setContact(prevState => ({
            ...prevState,
            university: [...newUniversities],
            contact_universities_attributes: [...newUniversities],
        }));
    }, [university])

    // Keyed off university_id: an imported row has no row id, and gating on one left the
    // autocomplete empty with only the years filled in.
    useEffect(() => {
        if (universityData.university_id) {
            setSelectedUniversity({ id: universityData.university_id, name: universityData.name });
        }
    }, [])

    const handleOnChangeAutocomplete = (field, selectedValue) => {
        handleUserChanges()
        let fieldData = {};

        switch (field) {
            case 'university_id':
                fieldData = {
                    university_id: selectedValue?.id,
                }

                setSelectedUniversity(selectedValue);
                break;
            default:
                fieldData = {[field]: selectedValue};
                break;
        }

        setUniversity(prevState => ({
            ...prevState,
            ...fieldData,
        }));
    }

    const handleRemoveUniversity = () => {
        if (window.confirm('Are you sure?')) {
            handleUserChanges()
            setUniversity(prevState => ({
                ...prevState,
                _destroy: true,
            }));
        }
    }

    const errorMessage = {};

    return (
        university._destroy === true ? null :
            <>
                <Grid item xs={4}>
                    <SingleAsyncAutoComplete
                        fetch={fetchers.universities}
                        handleOnChange={(value) => handleOnChangeAutocomplete('university_id', value)}
                        value={selectedUniversity}
                        label={'University'}
                        field={'university_id'}
                        autoHighlight
                    />
                </Grid>

                <Grid item xs={3}>
                    <SingleAutoComplete
                        options={years}
                        handleOnChange={(value) => handleOnChangeAutocomplete('start_year', value?.id || null)}
                        handleOnError={() => {}}
                        selectedId={university.start_year || ""}
                        field={'start_year'}
                        label={"Start Year"}
                        variant={"outlined"}
                        className="entity-autocomplete"
                        autoHighlight
                    />
                </Grid>

                <Grid item xs={3}>
                    <SingleAutoComplete
                        options={[{id: null, name: "Present"}, ...years]}
                        handleOnChange={(value) => handleOnChangeAutocomplete('end_year', value?.id || null)}
                        handleOnError={() => {}}
                        selectedId={university.end_year || ''}
                        field={'end_year'}
                        label={"End Year"}
                        variant={"outlined"}
                        className="entity-autocomplete"
                        defaultOption={{id: null, name: "Present"}}
                        autoHighlight
                    />
                </Grid>

                <Grid item xs={2} style={{display: 'flex', alignItems: 'center'}}>
                    <Link onClick={handleRemoveUniversity}>Remove</Link>
                </Grid>
            </>
    )
}

const CompanyData = ({ companyData, companies, setContact, index, years, session, handleUserChanges }) => {
    const [company, setCompany] = useState({ ...companyData });
    const [selectedCompany, setSelectedCompany] = useState({});
    const fetchers = useDataFetchers(session);

    useEffect(() => {
        let newCompanies = [...companies]
        newCompanies[index] = company;
        setContact(prevState => ({
            ...prevState,
            company: [...newCompanies],
            company_contacts_attributes: [...newCompanies],
        }));
    }, [company])

    // Same as UniversityData: an imported row has no row id to gate on.
    useEffect(() => {
        if (companyData.company_id) {
            setSelectedCompany({ id: companyData.company_id, name: companyData.name });
        }
    }, [])

    const handleOnChangeAutocomplete = (field, selectedValue) => {
        handleUserChanges()
        let fieldData = {};

        switch (field) {
            case 'company_id':
                fieldData = {
                    company_id: selectedValue?.id,
                }

                setSelectedCompany(selectedValue);
                break;
            default:
                fieldData = {[field]: selectedValue};
                break;
        }

        setCompany(prevState => ({
            ...prevState,
            ...fieldData,
        }));
    }

    const handleRemoveCompany = () => {
        if (window.confirm('Are you sure?')) {
            handleUserChanges()
            setCompany(prevState => ({
                ...prevState,
                _destroy: true,
            }));
        }
    }

    const errorMessage = {};

    return (
        company._destroy === true ? null :
            <>
                <Grid item xs={4}>
                    <SingleAsyncAutoComplete
                        fetch={fetchers.companies}
                        handleOnChange={(value) => handleOnChangeAutocomplete('company_id', value)}
                        value={selectedCompany}
                        label={'Company'}
                        field={'company_id'}
                        autoHighlight
                    />
                </Grid>

                <Grid item xs={3}>
                    <SingleAutoComplete
                        options={years}
                        handleOnChange={(value) => handleOnChangeAutocomplete('start_year', value?.id || null)}
                        handleOnError={() => {}}
                        selectedId={company.start_year || ""}
                        field={'start_year'}
                        label={"Start Year"}
                        variant={"outlined"}
                        className="entity-autocomplete"
                        autoHighlight
                    />
                </Grid>

                <Grid item xs={3}>
                    <SingleAutoComplete
                        options={[{id: null, name: "Present"}, ...years]}
                        handleOnChange={(value) => handleOnChangeAutocomplete('end_year', value?.id || null)}
                        handleOnError={() => {}}
                        selectedId={company.end_year || ''}
                        field={'end_year'}
                        label={"End Year"}
                        variant={"outlined"}
                        className="entity-autocomplete"
                        defaultOption={{id: null, name: "Present"}}
                        autoHighlight
                    />
                </Grid>

                <Grid item xs={2} style={{display: 'flex', alignItems: 'center'}}>
                    <Link onClick={handleRemoveCompany}>Remove</Link>
                </Grid>
            </>
    )
}

export default Contact;